import React, { useState } from 'react';
import APModal from 'components/Modals/APModal/APModal';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import { Trans, useTranslation } from 'react-i18next';
import PaymentsService from 'services/Payments/payments.service';

import { PaymentDto, PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { Alert } from '@alpha/ui-lib/ui/Alert/Alert';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { Box, Divider } from '@alpha/ui-lib/ui/external';
import { StyledKeyValueTable } from '@alpha/ui-lib/ui/table';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useVOPConfirmation from './useVOPConfirmation';
import useStyles from './VOPConfirmationModal.styles';
import formatIsoDate from 'utils/formatIsoDate';
import { faExclamationTriangle, faArrowRight } from '@fortawesome/pro-regular-svg-icons';

type VOPConfirmationModalProps = {
  payment: PaymentDto | undefined;
  open: boolean;
  handleClose: () => void;
  handleActionButton?: (data: {
    paymentId: string;
    vopDetails?: PaymentDto['vopDetails'];
    vopRequired?: boolean;
    recommended?: boolean;
  }) => void;
  readOnly?: boolean;
};

const VOPConfirmationModal = ({
  payment,
  open,
  handleClose,
  handleActionButton,
  readOnly = false,
}: VOPConfirmationModalProps): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { icon, title, message, color, closeMatchTable, matchedTable } =
    useVOPConfirmation(payment);

  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const sb = useAlphaSnackbar();

  const canAcceptRecommendations =  payment?.id && payment?.vopDetails?.status === PaymentVopMatchStatus.NEAR_MATCH;

  const handleAcceptRecommendations = async (paymentId: string, vopDetails: PaymentDto['vopDetails'] | undefined) => {
    setIsAcknowledging(true);
    if (!vopDetails?.name) {
      sb.trigger(t('Error accepting Beneficiary name recommendation'), 'error');
      return;
    }
    PaymentsService.putAcceptVopRecommendations(paymentId, vopDetails.name)
    .then(() => {
      sb.trigger(t('beneficiary_cop_recommendation_accepted'), 'success');
      acknowledgeAndContinue(paymentId, true);
    })
    .catch((error) => {
      console.error(error);
      setIsAcknowledging(false);
      sb.trigger(t('error_default'), 'error');
    });
}

  const acknowledgeAndContinue = (id: string, recommended?: boolean) => {
    setIsAcknowledging(true);
    PaymentsService.patchAcknowledgePayment(id)
      .then(() => {
        handleActionButton?.({
          paymentId: id,
          vopDetails: payment?.vopDetails,
          vopRequired: payment?.vopRequired,
          recommended: !!recommended,
        });
      })
      .catch((error) => {
        console.error(error);
        sb.trigger(t('error_default'), 'error');
      })
      .finally(() => {
        setIsAcknowledging(false);
      });
  };

  const vopApprover = payment?.vopDetails?.vopApproverName;
  const vopDate = payment?.vopDetails?.vopApprovalDateTime
    ? formatIsoDate(payment?.vopDetails?.vopApprovalDateTime)
    : '-';

  return (
    <APModal.Container
      open={open}
      onClose={handleClose}
      className={styles.container}
    >
      <>
        <APModal.Header
          onClose={handleClose}
          className={styles.header}
          showCloseButton
        >
          <Box className={styles.header}>
            <Typography variant='h2'>
              <FontAwesomeIcon
                icon={icon}
                color={color}
                size='sm'
                style={{ marginRight: '8px' }}
              />
              {title}
            </Typography>
            <Typography variant='body1'>
              <Trans
                components={{ strong: <strong /> }}
                values={{
                  name: payment?.beneficiaryName ?? '',
                  correct_name: payment?.vopDetails?.name ?? '',
                }}
              >
                {message}
              </Trans>
            </Typography>
          </Box>
        </APModal.Header>
        <APModal.Wrapper className={styles.wrapper}>
          <Box className={styles.body}>
            <Typography variant='h3' style={{ position: 'relative' }} />

            {closeMatchTable.length > 0 && (
              <>
                {/* Showing what's different from the provided details */}
                <StyledKeyValueTable
                  testId='close-match-table'
                  className={styles.noDivider}
                  data={closeMatchTable}
                />

                <Divider
                  style={{
                    marginTop: '20px',
                    backgroundColor: '#f7f7f7',
                    marginBottom: '24px',
                  }}
                />
              </>
            )}

            {/* Showing same / completely different details  */}
            <StyledKeyValueTable
              testId='matching-table'
              className={[styles.noDivider, styles.matchingTable].join(' ')}
              data={matchedTable}
            />

            {!readOnly && (
              <Box className={styles.vopWarning}>
                <Alert
                  variant='warning'
                  text={
                    <>
                      <strong>{t('please_note')}</strong>{' '}
                      {t('cop_alert_message')}
                    </>
                  }
                />
              </Box>
            )}

            {readOnly && payment?.vopDetails?.vopApprovalAcknowledged && (
              <Box className={styles.vopInfo}>
                <Alert
                  variant='info'
                  text={
                    <Typography>
                      {<strong>{t('please_note')}</strong>}{' '}
                      {t('cop_alert_message_acknowledged')} {vopApprover}{' '}
                      {t('on')} {vopDate}.
                    </Typography>
                  }
                />
              </Box>
            )}
  
            {canAcceptRecommendations && !readOnly && (
            <Box marginTop="20px" justifyContent="right" display="flex">
              <ActionButton
                onClick={() => {if (payment?.id) { acknowledgeAndContinue(payment.id); }}}
                className={styles.continueWithoutUpdateButton}
                startIcon={<FontAwesomeIcon icon={faExclamationTriangle} color='#cf752f' />}
                endIcon={<FontAwesomeIcon icon={faArrowRight} color='#cf752f' />}
                disabled={isAcknowledging}
              >
                {t('continue_with_existing')}
              </ActionButton>
            </Box>)}
          </Box>
        </APModal.Wrapper>

        <Box className={styles.buttonsContainer}>
          {!readOnly && (
            <>
              <ActionButton onClick={handleClose} 
              className='cancelButton' 
              disabled={isAcknowledging}>
                {t('go_back')}
              </ActionButton>
              {!canAcceptRecommendations ? (
              <ActionButton
                testId='modal-action-button'
                disabled={isAcknowledging}
                startIcon={<FontAwesomeIcon icon={faExclamationTriangle} color='#cf752f' />}
                endIcon={<FontAwesomeIcon icon={faArrowRight} color='#cf752f' />}
                onClick={() => {
                  if (payment?.id) {
                    acknowledgeAndContinue(payment.id);
                  }
                }}
                className='submitButton'
                loading={isAcknowledging}
              >
                {t('Acknowledge & Continue')}
              </ActionButton>) : (
                <ActionButton
                 disabled={isAcknowledging}
                 loading={isAcknowledging}
                  testId='modal-action-button'
                  onClick={() => {
                    if (canAcceptRecommendations) {
                      handleAcceptRecommendations(payment?.id, payment?.vopDetails ?? undefined);
                    }
                  }}
                  className='submitButton'
                >
                  {t('accept_change_and_continue')}
                </ActionButton>
              )}
            </>
          )}
          {/* READ ONLY MODAL */}
          {readOnly && (
            <ActionButton
              onClick={handleClose}
              className='cancelButton'
              fullWidth
            >
              {t('close')}
            </ActionButton>
          )}
        </Box>
      </>
    </APModal.Container>
  );
};

export default VOPConfirmationModal;
